/**
 * CallStatsWidget — compact dashboard card with call statistics
 *
 * Shows: calls today (answered/missed), callback queue, avg duration.
 * Auto-refreshes every 60 seconds.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

interface CallStats {
  today: { total: number; answered: number; missed: number; avgDuration: number };
  week: { total: number; answered: number; missed: number; avgDuration: number };
  callbackQueue: number;
  byBrand: Record<string, number>;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export function CallStatsWidget() {
  const [stats, setStats] = useState<CallStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchStats = useCallback(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) return;

      const res = await fetch('/api/telephony/stats', {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setStats(data);
      }
    } catch (err) {
      console.error('[CallStats] fetch error:', err);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  if (isLoading || !stats) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5 animate-pulse">
        <div className="h-4 bg-slate-200 rounded w-24 mb-4" />
        <div className="grid grid-cols-3 gap-4">
          <div className="h-12 bg-slate-100 rounded" />
          <div className="h-12 bg-slate-100 rounded" />
          <div className="h-12 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
          </svg>
          <h3 className="text-sm font-semibold text-slate-900">Telefonie vandaag</h3>
        </div>
        {stats.callbackQueue > 0 && (
          <Link
            to="/inbox"
            className="text-xs font-medium text-red-600 bg-red-50 px-2 py-1 rounded-md hover:bg-red-100 transition-colors"
          >
            {stats.callbackQueue} terugbellen
          </Link>
        )}
      </div>

      <div className="grid grid-cols-3 gap-4">
        {/* Answered */}
        <div>
          <p className="text-2xl font-semibold text-emerald-600">{stats.today.answered}</p>
          <p className="text-xs text-slate-500 mt-0.5">Beantwoord</p>
        </div>

        {/* Missed */}
        <div>
          <p className="text-2xl font-semibold text-red-500">{stats.today.missed}</p>
          <p className="text-xs text-slate-500 mt-0.5">Gemist</p>
        </div>

        {/* Avg duration */}
        <div>
          <p className="text-2xl font-semibold text-slate-700">
            {stats.today.avgDuration > 0 ? formatDuration(stats.today.avgDuration) : '-'}
          </p>
          <p className="text-xs text-slate-500 mt-0.5">Gem. duur</p>
        </div>
      </div>

      {/* Brand breakdown */}
      {Object.keys(stats.byBrand).length > 0 && (
        <div className="mt-3 pt-3 border-t border-slate-100 flex items-center gap-3">
          {Object.entries(stats.byBrand).map(([brand, count]) => (
            <span key={brand} className="text-xs text-slate-500">
              <span className="font-medium text-slate-700">{count}</span> {brand}
            </span>
          ))}
        </div>
      )}

      {/* Week summary */}
      <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
        <p className="text-xs text-slate-400">
          Deze week: {stats.week.total} gesprekken ({stats.week.answered} beantwoord, {stats.week.missed} gemist)
        </p>
        <Link
          to="/telefoon"
          className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
        >
          Alle gesprekken →
        </Link>
      </div>
    </div>
  );
}
